import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  AutoEvaluation,
  AutoEvaluationLabel,
  JobDescription,
  showAllTypes,
} from '../../../../../../utils/types/JobOpenings';
import AddOrRemoveListWithOperators from '../../../ui/AddOrRemoveListWithOperators';
import Collapsible from '../../../ui/CollapsibleChildren';

interface CollapsibleData {
  id: number;
  title: string;
  labels: AutoEvaluationLabel[];
  isOpen: boolean;
  stringLabels?: string[];
}

type ListFieldsProps = {
  showAll: showAllTypes;
  setShowAll: React.Dispatch<React.SetStateAction<showAllTypes>>;
  autoEvaluation: AutoEvaluation;
  setAutoEvaluation: React.Dispatch<React.SetStateAction<AutoEvaluation>>;
  disabled?: boolean;
  jobDescription: JobDescription;
  evaluationType: 'Auto Suitability' | 'Auto Outstanding';
};

const ListFields = ({
  showAll,
  setShowAll,
  autoEvaluation,
  setAutoEvaluation,
  disabled = false,
  jobDescription,
  evaluationType,
}: ListFieldsProps) => {
  const { t } = useTranslation(NS.adminPages);
  const LF = ADMIN_PAGES_KEYS.jobOpenings.listFields;
  const SECTION_TITLE_KEYS: Record<string, string> = {
    'Educations': LF.educations,
    'Certifications': LF.certifications,
    'Skills': LF.skills,
    'School': LF.school,
    'Keywords': LF.keywords,
    'Behavioral Attributes': LF.behavioralAttributes,
  };
  const collapsibleValues = [
    {
      id: 1,
      title: 'Educations',
      labels: autoEvaluation?.educations ?? [],
      isOpen: true,
    },
    {
      id: 2,
      title: 'Certifications',
      labels: autoEvaluation?.certifications ?? [],
      isOpen: true,
    },
    {
      id: 3,
      title: 'Skills',
      labels: autoEvaluation?.skills ?? [],
      isOpen: true,
    },
    {
      id: 4,
      title: 'School',
      labels: autoEvaluation?.school ?? [],
      isOpen: true,
    },
    {
      id: 5,
      title: 'Keywords',
      labels: autoEvaluation?.keywords ?? [],
      isOpen: true,
    },
    {
      id: 6,
      title: 'Behavioral Attributes',
      labels: autoEvaluation?.behavioralAttributes ?? [],
      isOpen: true,
    },
  ];
  const [collapsible, setCollapsible] =
    useState<CollapsibleData[]>(collapsibleValues);

  useEffect(() => {
    const newIsOpenState = getNewIsOpenState(showAll);
    if (newIsOpenState === undefined) return;

    const newCollapsibleValues = collapsibleValues.map((collapsibleItem) => ({
      ...collapsibleItem,
      isOpen: newIsOpenState,
    }));

    setCollapsible((prevCollapsible) => {
      if (
        JSON.stringify(newCollapsibleValues) !== JSON.stringify(prevCollapsible)
      ) {
        return newCollapsibleValues;
      }
      return prevCollapsible;
    });
  }, [autoEvaluation, showAll]);

  useEffect(() => {
    const updatedAutoSuitability = { ...autoEvaluation };

    collapsible.forEach((item) => {
      switch (item.title) {
        case 'Educations':
          updatedAutoSuitability.educations = item.labels;
          break;
        case 'Certifications':
          updatedAutoSuitability.certifications = item.labels;
          break;
        case 'Skills':
          updatedAutoSuitability.skills = item.labels;
          break;
        case 'School':
          updatedAutoSuitability.school = item.labels;
          break;
        case 'Desirable Skills':
          updatedAutoSuitability.desirableSkills = item.labels;
          break;
        case 'Keywords':
          updatedAutoSuitability.keywords = item.labels;
          break;
        case 'Behavioral Attributes':
          updatedAutoSuitability.behavioralAttributes = item.labels;
          break;
        default:
          break;
      }
    });

    setAutoEvaluation(updatedAutoSuitability);
  }, [collapsible]);

  const getNewIsOpenState = (showAll: showAllTypes) => {
    if (showAll === 'showAll') {
      return true;
    } else if (showAll === 'collapseAll') {
      return false;
    }
    return;
  };

  const toggleOpen = (id: number) => {
    const newCollapsible = collapsible.map((collapsible) =>
      collapsible.id === id
        ? { ...collapsible, isOpen: !collapsible.isOpen }
        : collapsible,
    );
    setCollapsible(newCollapsible);
    const areAllOpen = newCollapsible.every(
      (collapsible) => collapsible.isOpen,
    );
    const areAllClosed = newCollapsible.every(
      (collapsible) => !collapsible.isOpen,
    );
    if (areAllOpen) {
      setShowAll('showAll');
    } else if (areAllClosed) {
      setShowAll('collapseAll');
    } else {
      setShowAll('random');
    }
  };

  const handleLabelsChange = (
    id: number,
    updatedLabels: AutoEvaluationLabel[],
  ) => {
    setCollapsible(
      collapsible.map((collapsible) =>
        collapsible.id === id
          ? { ...collapsible, labels: updatedLabels }
          : collapsible,
      ),
    );
  };

  const handleBulkToggleOperator = (id: number, operator: 'AND' | 'OR') => {
    const section = collapsible.find((c) => c.id === id);
    if (!section) return;

    const updatedLabels = section.labels.map((label) => ({
      ...label,
      relationToNext: operator,
    }));

    handleLabelsChange(id, updatedLabels);
  };

  const LogicToggle = ({
    id,
    labels,
  }: {
    id: number;
    labels: AutoEvaluationLabel[];
  }) => {
    if (labels.length < 2) return null;

    const isAllAnd = labels.every((l) => l.relationToNext === 'AND');
    const isAllOr = labels.every((l) => l.relationToNext === 'OR');

    return (
      <div className='flex bg-gray-100 p-0.5 rounded-md border border-gray-200'>
        <button
            onClick={() => handleBulkToggleOperator(id, 'AND')}
            className={`px-2 py-0.5 text-[10px] font-bold rounded transition-all duration-200 ${
              isAllAnd
                ? 'bg-emerald-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-600'
            }`}
        >
          AND
        </button>
        <button
            onClick={() => handleBulkToggleOperator(id, 'OR')}
            className={`px-2 py-0.5 text-[10px] font-bold rounded transition-all duration-200 ${
              isAllOr
                ? 'bg-emerald-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-600'
            }`}
        >
          OR
        </button>
      </div>
    );
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr'>
      { collapsible.map(({ id, title, labels, isOpen }, index) => (
        <div
            key={id}
            className='flex flex-col h-full relative transition-all duration-300 transform hover:translate-y-[-2px]'
        >
          <Collapsible
              title={`${t(SECTION_TITLE_KEYS[title] ?? title)} ${labels.length > 0 ? `(${labels.length})` : ''}`}
              isOpen={isOpen}
              toggleOpen={() => toggleOpen(id)}
              headerActions={<LogicToggle id={id} labels={labels} />}
          >
            <AddOrRemoveListWithOperators
                disabled={disabled}
                key={JSON.stringify(labels)}
                initialLabels={labels}
                onLabelsChange={(updatedLabels) =>
                  handleLabelsChange(id, updatedLabels)
                }
                desirableSkills={
                  title === 'Skills' ? jobDescription.desirableSkills : undefined
                }
            />
          </Collapsible>
        </div>
      )) }
    </div>
  );
};

export default ListFields;
